import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";
import { Box, Button, TextField, Typography, Table, TableHead, TableRow, TableCell, TableBody } from "@material-ui/core";
import { BaseModal } from "Components/Modal/BaseModal";
import { CancelButton } from "Globals/Styles/Control/Buttons";
import styled from "styled-components";
import { DailyProgrammeProgressUpdateV2ViewModel } from "./DailyProgrammeProgressUpdateV2ViewModel";
import { DarwinInputWrapper } from "Components/Form/DarwinInputWrapper";
import { DailyProgrammeProgressUpdateV2ItemModel } from "./DailyProgrammeProgressUpdateV2ItemModel";
import { Cell, Grid, useRouter } from "@shoothill/core";

interface IModalProps {
    open: boolean;
    onClose(): void;
    date: Date;
    formId: string;
}

export const DailyProgrammeProgressUpdateV2View: React.FC<IModalProps> = (props) => {
    const { match } = useRouter();
    const { open, onClose, date, formId } = props;
    const { projectid } = match.params as any;
    const [viewModel] = useState(() => new DailyProgrammeProgressUpdateV2ViewModel(projectid, date, formId));

    const COLUMNS8 = "1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr";

    const handleAdd = (e: React.FormEvent) => {
        e.preventDefault();
        viewModel.addItem();
    };

    const handleSave = async () => {
        if (await viewModel.isMyModelValid()) {
            const result = await viewModel.upsert();
            if (result && result.wasSuccessful) {
                onClose();
            }
        }
    };

    return useObserver(() => (
        <BaseModal
            open={open}
            onClose={onClose}
            title={"Daily programme progress update"}
            actions={
                <>
                    <Button onClick={handleSave} color="secondary" autoFocus variant="contained" disabled={viewModel.IsLoading}>
                        Save
                    </Button>
                    <CancelButton onClick={onClose} disabled={viewModel.IsLoading}>
                        Cancel
                    </CancelButton>
                </>
            }
        >
            <FormSection>
                <form onSubmit={handleAdd} style={{ marginBottom: 24 }}>
                    <Grid columnGap={30} rowGap={20} tc={COLUMNS8} dc={COLUMNS8} marginBottom={"10px"}>
                        <Cell ts={2} ds={2}>
                            <DarwinInputWrapper<DailyProgrammeProgressUpdateV2ItemModel>
                                type="text"
                                label="Trade"
                                placeHolder="Trade"
                                validateOnBlur={true}
                                viewModel={viewModel.newItem}
                                fieldName="trade"
                                shrink={true}
                                maxLength={256}
                                editMode={!viewModel.IsLoading}
                                helperText={viewModel.newItem.getError("trade")}
                            />
                        </Cell>
                        <Cell ts={2} ds={2}>
                            <DarwinInputWrapper<DailyProgrammeProgressUpdateV2ItemModel>
                                type="text"
                                label="Activity"
                                placeHolder="Activity"
                                validateOnBlur={true}
                                viewModel={viewModel.newItem}
                                fieldName="activity"
                                shrink={true}
                                maxLength={256}
                                editMode={!viewModel.IsLoading}
                                helperText={viewModel.newItem.getError("activity")}
                            />
                        </Cell>
                        <Cell ts={2} ds={2}>
                            <DarwinInputWrapper<DailyProgrammeProgressUpdateV2ItemModel>
                                type="number"
                                label="% completion"
                                placeHolder="% completion"
                                validateOnBlur={true}
                                viewModel={viewModel.newItem}
                                fieldName="percentageCompletion"
                                shrink={true}
                                maxLength={3}
                                editMode={!viewModel.IsLoading}
                                helperText={viewModel.newItem.getError("percentageCompletion")}
                            />
                        </Cell>
                        <Cell ts={2} ds={2}>
                            <Button type="submit" color="primary" variant="contained" disabled={viewModel.IsLoading}>
                                Add
                            </Button>
                        </Cell>
                    </Grid>
                    {viewModel.newItemError && (
                        <Typography color="error" variant="body2" style={{ marginTop: 4 }}>
                            {viewModel.newItemError}
                        </Typography>
                    )}
                </form>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Trade</TableCell>
                            <TableCell>Activity</TableCell>
                            <TableCell>% completion</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {viewModel.items.map((itemVM, idx) => (
                            <TableRow key={idx}>
                                <TableCell>{itemVM.model.trade}</TableCell>
                                <TableCell>{itemVM.model.activity}</TableCell>
                                <TableCell>
                                    <DarwinInputWrapper<DailyProgrammeProgressUpdateV2ItemModel>
                                        type="number"
                                        label=""
                                        placeHolder="% completion"
                                        validateOnBlur={true}
                                        viewModel={itemVM}
                                        fieldName="percentageCompletion"
                                        shrink={true}
                                        maxLength={3}
                                        editMode={!viewModel.IsLoading}
                                        helperText={itemVM.getError("percentageCompletion")}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </FormSection>
        </BaseModal>
    ));
};

const FormSection = styled(Box)`
    margin-bottom: 16px;
`;
